import { showConfirmDialog, showMessageBox } from '../../../components';
import { Button, IconButton } from '../../../components/controls';
import type { IconName } from '../../../components/display/Icon';
import { useStore } from '../../../store';
import PulseKey from '../store/PulseKey';

/** `labeled`: text buttons for a page instead of grid row icons */
export default function KeyActions({
  labeled,
  onChange,
  pulseKey,
}: {
  labeled?: boolean;
  onChange: () => unknown;
  pulseKey: PulseKey;
}) {
  return (
    <>
      <ResetDownloadCountButton
        labeled={labeled}
        onChange={onChange}
        pulseKey={pulseKey}
      />
      <ToggleDisableButton
        labeled={labeled}
        onChange={onChange}
        pulseKey={pulseKey}
      />
      <UnassignButton
        labeled={labeled}
        onChange={onChange}
        pulseKey={pulseKey}
      />
      <ChangeCustomerButton
        labeled={labeled}
        onChange={onChange}
        pulseKey={pulseKey}
      />
      <SendPulseKeyButton labeled={labeled} pulseKey={pulseKey} />
    </>
  );
}

function UnassignButton({
  pulseKey,
  labeled,
  onChange,
}: {
  labeled?: boolean;
  onChange: () => unknown;
  pulseKey: PulseKey;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    await pulseKeyStore.unassignKey(pulseKey);
    await onChange();
  }

  return (
    <ActionButton
      icon="user-times"
      label="Unassign"
      labeled={labeled}
      onClick={onClick}
      title="Revoke customer assignment"
    />
  );
}

function ToggleDisableButton({
  pulseKey,
  labeled,
  onChange,
}: {
  labeled?: boolean;
  onChange: () => unknown;
  pulseKey: PulseKey;
}) {
  const { pulseKeyStore } = useStore();
  const { disabled } = pulseKey;

  async function onEnableClick() {
    await pulseKeyStore.enableKey(pulseKey);
    await onChange();
  }

  async function onDisableClick() {
    await pulseKeyStore.disableKey(pulseKey);
    await onChange();
  }

  if (disabled) {
    return (
      <ActionButton
        icon="check-circle"
        label="Enable"
        labeled={labeled}
        onClick={onEnableClick}
        title="Enable key. A user will be able to use it again."
      />
    );
  }

  return (
    <ActionButton
      icon="ban"
      label="Disable"
      labeled={labeled}
      onClick={onDisableClick}
      title="Disable key. Disabled key can't be redeemed."
    />
  );
}

function ResetDownloadCountButton({
  pulseKey,
  labeled,
  onChange,
}: {
  labeled?: boolean;
  onChange: () => unknown;
  pulseKey: PulseKey;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    await pulseKeyStore.resetKeyDownloadCount(pulseKey);
    await onChange();
  }

  return (
    <ActionButton
      icon="creative-commons-zero"
      label="Reset downloads"
      labeled={labeled}
      onClick={onClick}
      title="Reset download count"
    />
  );
}

function ChangeCustomerButton({
  pulseKey,
  labeled,
  onChange,
}: {
  labeled?: boolean;
  onChange: () => unknown;
  pulseKey: PulseKey;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    const email = await showConfirmDialog({
      title: 'Change customer',
      textField: {
        label: 'Email of a new customer',
      },
    });

    if (email !== 'cancel') {
      await pulseKeyStore.changeKeyCustomer(pulseKey, email);
      await onChange();
    }
  }

  return (
    <ActionButton
      icon="exchange-alt"
      label="Change customer"
      labeled={labeled}
      onClick={onClick}
      title="Assign key to another customer"
    />
  );
}

function SendPulseKeyButton({
  labeled,
  pulseKey,
}: {
  labeled?: boolean;
  pulseKey: PulseKey;
}) {
  const { pulseKeyStore } = useStore();

  async function onClick() {
    const buttonId = await showMessageBox({
      buttons: [
        { id: 'ok', title: 'send' },
        { id: 'cancel', title: 'cancel' },
      ],
      content:
        `Are you sure you want to send the key ` +
        `${pulseKey.key} to ${pulseKey.customerEmail}?`,
      title: 'Pulse Key send',
    });

    if (buttonId === 'ok') {
      return pulseKeyStore.sendKeyToTheCustomer(pulseKey);
    }

    return null;
  }

  return (
    <ActionButton
      icon="envelope"
      label="Send key"
      labeled={labeled}
      onClick={onClick}
      title="Send Pulse Key to the customer"
    />
  );
}

function ActionButton({
  icon,
  label,
  labeled,
  onClick,
  title,
}: {
  icon: IconName;
  label: string;
  labeled?: boolean;
  onClick: () => unknown;
  title: string;
}) {
  if (labeled) {
    return (
      <Button
        icon={icon}
        onClick={onClick}
        sx={{ whiteSpace: 'nowrap' }}
        title={title}
        type="secondary"
      >
        {label}
      </Button>
    );
  }

  return (
    <IconButton icon={icon} onClick={onClick} size="small" title={title} />
  );
}
